import { useRef, useState } from "react";
import ExcelJS from "exceljs";
import Modal from "../../../components/base/Modal";
import type { buildThemeClasses } from "../theme";
import { bulkImportLeads, type BulkImportResult } from "../services/leadsService";
import {
  trackLeadsImported,
  trackLeadsImportFailed,
} from "../../../analytics/crmAnalytics";

// ===========================================================================
// Bulk import modal — upload an Excel/CSV of contacts to create many leads at
// once. Imported contacts are flagged on the backend so they do NOT trigger the
// automated calling workflow (that's reserved for landing-form / API leads),
// but each one still opens a card on the Pipeline board.
//
// "Descargar plantilla" generates the canonical .xlsx template in-browser so
// the columns always match what the importer expects.
// ===========================================================================

type TC = ReturnType<typeof buildThemeClasses>;

// Template columns — header label + an example value. The backend matches these
// headers case-/accent-insensitively (see crmLeadsController BULK_HEADER_MAP).
const TEMPLATE_COLUMNS: { header: string; width: number; example: string }[] = [
  { header: "Nombre", width: 22, example: "Juan Pérez" },
  { header: "Empresa", width: 22, example: "Acme S.A. de C.V." },
  { header: "Email", width: 28, example: "juan.perez@acme.com" },
  { header: "WhatsApp", width: 18, example: "+52 55 1234 5678" },
  { header: "Cargo", width: 18, example: "Gerente de Operaciones" },
  { header: "Sistema de interés", width: 22, example: "Shopify" },
  { header: "Servicio de interés", width: 22, example: "CRM & Marketing" },
  { header: "Cómo nos conoce", width: 22, example: "Referido" },
  { header: "Referido por", width: 20, example: "Ana de Beta Corp" },
  { header: "Tamaño de empresa", width: 20, example: "11-50 empleados" },
  { header: "Problema", width: 30, example: "Automatizar la toma de pedidos" },
  { header: "Método de contacto", width: 20, example: "WhatsApp" },
  { header: "Producto", width: 16, example: "Coding Cloud" },
];

const SECOND_EXAMPLE: Record<string, string> = {
  Nombre: "María López",
  Empresa: "Beta Corp",
  Email: "maria.lopez@betacorp.com",
  WhatsApp: "",
  Cargo: "Directora Comercial",
  "Sistema de interés": "WooCommerce",
  "Servicio de interés": "Facturación",
  "Cómo nos conoce": "Búsqueda en Google",
  "Referido por": "",
  "Tamaño de empresa": "51-200 empleados",
  Problema: "Integrar el CRM con su tienda",
  "Método de contacto": "Email",
  Producto: "Wayak",
};

export async function downloadTemplate(): Promise<void> {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Contactos");

  ws.columns = TEMPLATE_COLUMNS.map((c) => ({ header: c.header, key: c.header, width: c.width }));
  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: "middle" };

  ws.addRow(TEMPLATE_COLUMNS.reduce((r, c) => ((r[c.header] = c.example), r), {} as Record<string, string>));
  ws.addRow(SECOND_EXAMPLE);

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "plantilla-contactos-crm.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function LeadsImportModal({
  tc,
  onClose,
  onImported,
}: {
  tc: TC;
  onClose: () => void;
  onImported: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pick = (f: File | null) => {
    setError(null);
    setResult(null);
    if (!f) return setFile(null);
    const ok = /\.(xlsx|csv)$/i.test(f.name);
    if (!ok) {
      setError("Formato no soportado. Usa un archivo .xlsx o .csv.");
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) return;
    setImporting(true);
    setError(null);
    // Solo la extension: el nombre del archivo suele ser el del cliente.
    const fileType = /\.xlsx$/i.test(file.name) ? "xlsx" : "csv";
    try {
      const res = await bulkImportLeads(file);
      trackLeadsImported({
        fileType,
        created: res.created,
        skipped: res.skipped,
      });
      setResult(res);
      if (res.created > 0) onImported();
    } catch (e) {
      trackLeadsImportFailed(fileType);
      const err = e as { response?: { data?: { data?: { message?: string }; message?: string } }; message?: string };
      setError(
        err.response?.data?.data?.message ||
          err.response?.data?.message ||
          err.message ||
          "No se pudo importar el archivo",
      );
    } finally {
      setImporting(false);
    }
  };

  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content`;

  return (
    <Modal
      title="Importar contactos"
      onClose={onClose}
      themeClasses={tc}
      content={
        <div className="space-y-4">
          {/* Step 1 — template */}
          <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-500">
                <i className="ri-file-excel-2-line text-lg" />
              </span>
              <div className="flex-1">
                <p className={`text-sm font-semibold ${tc.text}`}>1. Descarga la plantilla</p>
                <p className={`mt-0.5 text-xs ${tc.textSecondary}`}>
                  Llena una fila por contacto. <b>Nombre</b> y al menos <b>Email</b> o{" "}
                  <b>WhatsApp</b> son obligatorios. Las demás columnas son opcionales.
                </p>
                <button
                  onClick={() => void downloadTemplate()}
                  className="mt-2 inline-flex items-center gap-2 rounded-lg border border-emerald-500 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-500/10"
                >
                  <i className="ri-download-2-line" /> Descargar plantilla (.xlsx)
                </button>
              </div>
            </div>
          </div>

          {/* Step 2 — upload */}
          <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
            <p className={`text-sm font-semibold ${tc.text}`}>2. Sube el archivo</p>
            <p className={`mt-0.5 mb-2 text-xs ${tc.textSecondary}`}>
              Acepta <b>.xlsx</b> o <b>.csv</b>. Los contactos importados no disparan llamadas
              automáticas, pero sí generan una tarjeta en el Pipeline.
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.csv"
              onChange={(e) => pick(e.target.files?.[0] ?? null)}
              className={inputClass}
            />
            {file && (
              <p className={`mt-2 text-xs ${tc.textSecondary}`}>
                <i className="ri-attachment-2 mr-1" />
                {file.name}
              </p>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500">
              <i className="ri-error-warning-line mr-1" />
              {error}
            </div>
          )}

          {/* Result summary */}
          {result && (
            <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <p className={`mb-2 text-sm font-semibold ${tc.text}`}>Resultado de la importación</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat label="Filas" value={result.total} tone="text-sky-500" />
                <Stat label="Creados" value={result.created} tone="text-emerald-500" />
                <Stat label="Omitidos" value={result.skipped} tone="text-amber-500" />
              </div>
              {result.errors.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto rounded-lg border border-amber-500/30 bg-amber-500/5 p-2">
                  <p className="mb-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                    Filas con problemas:
                  </p>
                  <ul className={`space-y-0.5 text-xs ${tc.textSecondary}`}>
                    {result.errors.map((err, i) => (
                      <li key={i}>
                        {err.row ? `Fila ${err.row}: ` : ""}
                        {err.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}
            >
              {result ? "Cerrar" : "Cancelar"}
            </button>
            {!result && (
              <button
                onClick={() => void submit()}
                disabled={!file || importing}
                className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <i className={importing ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} />
                {importing ? "Importando..." : "Importar contactos"}
              </button>
            )}
          </div>
        </div>
      }
    />
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="rounded-lg border border-line/40 p-2">
      <div className={`text-lg font-bold ${tone}`}>{value}</div>
      <div className="text-[11px] text-gray-500">{label}</div>
    </div>
  );
}
